import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Tooltip,
  Divider,
  Collapse,
  Menu,
  MenuItem,
  useMediaQuery,
  Select,
  FormControl,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { LOGIN_DOMAIN_KEY, rotaLogin } from './Login';
import { clearAuth, getAuthUserName, getAuthSlug, getAuthPermissoes } from '../services/authStorage';
import { logoutSession } from '../services/api';
import useModulosLicenca from '../hooks/useModulosLicenca';
import { useEstabelecimento } from '../context/EstabelecimentoContext';

import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import StorageIcon from '@mui/icons-material/Storage';
import CategoryIcon from '@mui/icons-material/Category';
import SellIcon from '@mui/icons-material/Sell';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BuildIcon from '@mui/icons-material/Build';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import ScaleIcon from '@mui/icons-material/Scale';
import TableViewIcon from '@mui/icons-material/TableView';
import StyleIcon from '@mui/icons-material/Style';
import BugReportIcon from '@mui/icons-material/BugReport';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';

import { ThemeModeToggle } from '../theme/ThemeModeProvider';

export const DRAWER_WIDTH = 260;
export const DRAWER_WIDTH_COLLAPSED = 72;

const SECTIONS = [
  { label: 'Dashboard', icon: <HomeIcon />, route: '/dashboard' },
  {
    label: 'Produtos',
    icon: <StorageIcon />,
    children: [
      { label: 'Lista de Produtos', route: '/products', icon: <SellIcon /> },
      { label: 'Categorias', route: '/categories', icon: <CategoryIcon /> },
      { label: 'Subcategorias', route: '/subcategories', icon: <LocalOfferIcon /> },
      { label: 'Marcas', route: '/brands', icon: <LocalOfferIcon /> },
      { label: 'Campanhas Promocionais', route: '/campaigns', icon: <AssignmentTurnedInIcon /> },
    ],
  },
  {
    label: 'Estoque',
    icon: <Inventory2Icon />,
    modulo: 'MOD_ESTOQUE',
    children: [
      { label: 'Movimento de Estoque', route: '/stock-movement', icon: <AssignmentTurnedInIcon />, permissions: ['movimentaEstoque', 'inventarioEstoque'], modulo: 'MOD_ESTOQUE' },
      { label: 'Locais de estoque', route: '/stock-locations', icon: <StorageIcon />, modulo: 'ADD_MULTI_ESTOQUE' },
      { label: 'Transferências', route: '/stock-transfers', icon: <LocalShippingIcon />, permissions: ['transferenciaConsultar', 'transferenciaCriar', 'transferenciaSeparar', 'transferenciaConcluir'], modulo: 'ADD_MULTI_ESTOQUE' },
      { label: 'Situação de estoque', route: '/reports/estoque-situacao', icon: <InsightsIcon />, permissions: ['estoqueRelatorio'], modulo: 'MOD_ESTOQUE' },
      { label: 'Disponibilidade de estoque', route: '/reports/estoque-disponibilidade', icon: <TableViewIcon />, permissions: ['estoqueRelatorio'], modulo: 'MOD_ESTOQUE' },
      { label: 'Planejamento de compras', route: '/reports/planejamento-compras', icon: <InsightsIcon />, permissions: ['estoqueRelatorio'], modulo: 'MOD_ESTOQUE' },
    ],
  },
  {
    label: 'Clientes',
    icon: <PersonIcon />,
    children: [
      { label: 'Lista de Clientes', route: '/clients', icon: <PersonIcon /> },
      { label: 'Convênio', route: '/convenios', icon: <ReceiptLongIcon /> },
      { label: 'Vales troca', route: '/vales-troca', icon: <ReceiptLongIcon /> },
    ],
  },
  {
    label: 'Fornecedores',
    icon: <LocalShippingIcon />,
    children: [
      { label: 'Lista de Fornecedores', route: '/suppliers', icon: <LocalShippingIcon />, modulo: 'MOD_FORNECEDORES' },
      { label: 'Transportadoras', route: '/transportadoras', icon: <LocalShippingIcon />, modulo: 'MOD_FORNECEDORES' },
    ],
  },
  {
    label: 'Usuários',
    icon: <GroupsIcon />,
    children: [
      {
        label: 'Lista de Usuários',
        route: '/users',
        icon: <GroupsIcon />,
        permissions: ['gerenciaUsuarios', 'cadastraUsuario'],
      },
      {
        label: 'Grupos de Permissões',
        route: '/permission-profiles',
        icon: <AdminPanelSettingsIcon />,
        permissions: ['alteraPermissao'],
      },
    ],
  },
  {
    label: 'Fiscal',
    icon: <AccountBalanceIcon />,
    modulo: 'MOD_NFE',
    children: [
      {
        label: 'Emitir NF-e',
        route: '/fiscal/nfes',
        icon: <ReceiptLongIcon />,
        permissions: ['alteraParametros'],
        modulo: 'MOD_NFE',
      },
      {
        label: 'Manifesto / busca NF-e',
        route: '/fiscal/manifesto',
        icon: <FactCheckIcon />,
        permissions: ['movimentaEstoque', 'inventarioEstoque', 'alteraParametros'],
        modulo: 'MOD_NFE',
      },
      {
        label: 'Importar XML NF-e',
        route: '/fiscal/importar-xml',
        icon: <UploadFileIcon />,
        permissions: ['movimentaEstoque', 'inventarioEstoque'],
        modulo: 'MOD_NFE',
      },
      { label: 'CFOP', route: '/fiscal/cfops', icon: <ReceiptLongIcon />, modulo: 'MOD_NFE' },
      { label: 'Tabela IBPT', route: '/fiscal/ibpt', icon: <TableViewIcon />, modulo: 'MOD_NFE' },
      { label: 'Grupos tributários', route: '/tax-groups', icon: <AssignmentTurnedInIcon />, modulo: 'MOD_NFE' },
    ],
  },
  {
    label: 'Relatórios',
    icon: <AssessmentIcon />,
    children: [
      {
        label: 'Gerenciais',
        route: '/reports',
        icon: <AssessmentIcon />,
        permissions: ['relatorios'],
      },
      {
        label: 'Cancelamentos',
        route: '/reports/cancellations',
        icon: <CancelOutlinedIcon />,
        permissions: ['relatorios'],
      },
      {
        label: 'Curva ABC',
        route: '/reports/abc',
        icon: <InsightsIcon />,
        permissions: ['relatorios'],
      },
      {
        label: 'Documentos fiscais',
        route: '/reports/fiscal-documents',
        icon: <ReceiptLongIcon />,
        permissions: ['relatorios'],
      },
      {
        label: 'Auditoria',
        route: '/reports/audit',
        icon: <AssessmentIcon />,
        permissions: ['relatorios'],
      },
      {
        label: 'Transações ZenPay',
        route: '/reports/zenpay',
        icon: <PaymentIcon />,
        permissions: ['relatorios'],
        modulo: 'ADD_ZENPAY',
      },
      {
        label: 'Sessões de Caixa',
        route: '/cash-sessions',
        icon: <ReceiptLongIcon />,
        permissions: [
          'consultaResumo',
          'fechaCaixa',
          'movimentaCaixa',
          'editaMovimentoCaixa',
        ],
      },
      {
        label: 'Pré-vendas',
        route: '/pre-vendas',
        icon: <ReceiptLongIcon />,
        permissions: ['preVenda', 'consultaVendas', 'relatorios'],
        modulo: 'ADD_PREVENDA',
      },
    ],
  },
  {
    label: 'Orçamentos / Pedidos',
    icon: <RequestQuoteIcon />,
    modulo: 'ADD_ORCAMENTO',
    children: [
      {
        label: 'Orçamentos',
        route: '/orcamentos',
        icon: <RequestQuoteIcon />,
        permissions: ['orcamento'],
        modulo: 'ADD_ORCAMENTO',
      },
      {
        label: 'Pedidos de venda',
        route: '/pedidos-venda',
        icon: <ShoppingCartCheckoutIcon />,
        permissions: ['orcamento'],
        modulo: 'ADD_ORCAMENTO',
      },
    ],
  },
  {
    label: 'Utilitários',
    icon: <BuildIcon />,
    children: [
      { label: 'Visão geral', route: '/utilities', icon: <BuildIcon /> },
      { label: 'Carga de balança', route: '/utilities/scale-load', icon: <ScaleIcon /> },
      { label: 'Excel de produtos', route: '/utilities/excel', icon: <TableViewIcon /> },
      { label: 'Reajuste de produtos', route: '/utilities/reajuste-produtos', icon: <TuneIcon /> },
      { label: 'Excel de clientes', route: '/utilities/excel-clientes', icon: <PersonIcon /> },
      { label: 'Gerar etiquetas', route: '/utilities/labels', icon: <QrCode2Icon /> },
      { label: 'Modelos de etiqueta', route: '/utilities/label-models', icon: <StyleIcon /> },
      { label: 'Erros do PDV', route: '/utilities/pdv-erros', icon: <BugReportIcon /> },
    ],
  },
  {
    label: 'Administração',
    icon: <SettingsIcon />,
    children: [
      { label: 'Estabelecimentos', route: '/establishments', icon: <BusinessIcon /> },
      { label: 'PDVs', route: '/pdvs', icon: <PointOfSaleIcon /> },
      { label: 'Formas de Pagamento', route: '/payment-methods', icon: <PaymentIcon /> },
      { label: 'Integrações PIX', route: '/pix-integrations', icon: <QrCode2Icon /> },
    ],
  },
];

const ALL_ROUTES = SECTIONS.flatMap((section) =>
  section.children ? section.children.map((child) => child.route) : [section.route]
);

function rotaEhDesta(route, pathname) {
  return pathname === route || pathname.startsWith(`${route}/`);
}

function secaoContemRota(section, pathname) {
  if (!section.children) return false;
  return section.children.some((child) => {
    if (!rotaEhDesta(child.route, pathname)) return false;
    const outraMaisLonga = SECTIONS.some((outra) =>
      (outra.children || []).some(
        (ch) => ch.route.length > child.route.length && rotaEhDesta(ch.route, pathname),
      ),
    );
    return !outraMaisLonga;
  });
}

function Navbar({
  onMenuClick,
  isDesktop,
  drawerOpen,
  setDrawerOpen,
  collapsed = false,
  onToggleCollapse,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'));
  const zen = theme.zen;
  const slim = isDesktop && collapsed;
  const width = slim ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const userName = getAuthUserName() || 'Usuário';
  const permissions = getAuthPermissoes();
  const { isAtivo } = useModulosLicenca();
  const {
    estabelecimentos,
    estabelecimentoId,
    selecionarEstabelecimento,
    carregando: carregandoEstabs,
  } = useEstabelecimento();

  const canSeeModulo = (item) => !item.modulo || isAtivo(item.modulo);
  const canSee = (item) =>
    canSeeModulo(item) &&
    (!item.permissions ||
    item.permissions.some((permission) => permissions?.[permission]));
  const visibleSections = SECTIONS
    .map((section) =>
      section.children
        ? { ...section, children: section.children.filter(canSee) }
        : section
    )
    .filter((section) => canSee(section) && (!section.children || section.children.length));
  const initials = userName.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase();

  const [openSections, setOpenSections] = React.useState(() => {
    const obj = {};
    SECTIONS.forEach((s) => {
      obj[s.label] = secaoContemRota(s, location.pathname);
    });
    return obj;
  });

  const [flyout, setFlyout] = React.useState({
    anchor: null,
    section: null,
  });

  const toggleSection = (label) =>
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));

  const activeRoute = React.useMemo(() => {
    const matches = ALL_ROUTES.filter(
      (route) => location.pathname === route || location.pathname.startsWith(route + '/')
    );
    return matches.sort((a, b) => b.length - a.length)[0] || null;
  }, [location.pathname]);

  const isActive = (route) => route === activeRoute;
  const sectionHasActive = (section) =>
    section.children?.some((c) => isActive(c.route)) || isActive(section.route);

  const handleLogout = async () => {
    const slug = getAuthSlug() || localStorage.getItem(LOGIN_DOMAIN_KEY);
    try {
      await logoutSession();
    } catch {
      /* best effort */
    }
    clearAuth();
    navigate(rotaLogin(slug));
  };

  const goTo = (route) => {
    navigate(route);
    setFlyout({ anchor: null, section: null });
    if (isMobile) setDrawerOpen(false);
  };

  const baseItemSx = {
    borderRadius: 1.5,
    mx: slim ? 0.75 : 1,
    mb: 0.5,
    px: slim ? 1 : 2,
    justifyContent: slim ? 'center' : 'flex-start',
    color: zen.sidebarText,
    '&:hover': { backgroundColor: zen.sidebarHover },
    '&.Mui-selected': {
      backgroundColor: zen.sidebarSelected,
      color: zen.sidebarSelectedText,
      '& .MuiListItemIcon-root': { color: zen.sidebarSelectedText },
      '&:hover': { backgroundColor: zen.sidebarSelectedHover },
    },
  };

  const iconColor = {
    color: zen.sidebarIcon,
    minWidth: slim ? 0 : 36,
    justifyContent: 'center',
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: zen.sidebarBackground,
        color: zen.sidebarText,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: slim ? 'center' : 'flex-start',
          py: slim ? 1.5 : 2,
          px: slim ? 1 : 2.5,
          minHeight: 64,
          boxSizing: 'border-box',
        }}
      >
        {slim ? (
          <Box
            component="img"
            src={zen.sidebarSymbol}
            alt="ZenGestão"
            draggable={false}
            onClick={() => goTo('/dashboard')}
            sx={{
              width: 32,
              height: 32,
              objectFit: 'contain',
              cursor: 'pointer',
            }}
          />
        ) : (
          <Box
            component="img"
            src={zen.navbarLogo}
            alt="ZenGestão"
            draggable={false}
            onClick={() => goTo('/dashboard')}
            sx={{
              height: 32,
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              cursor: 'pointer',
            }}
          />
        )}
      </Box>

      <Divider sx={{ borderColor: zen.sidebarDivider }} />

      <List sx={{ flexGrow: 1, py: 1, overflowX: 'hidden' }}>
        {visibleSections.map((section) => {
          if (!section.children) {
            const button = (
              <ListItemButton
                key={section.label}
                onClick={() => goTo(section.route)}
                selected={isActive(section.route)}
                sx={baseItemSx}
              >
                <ListItemIcon sx={iconColor}>{section.icon}</ListItemIcon>
                {!slim && (
                  <ListItemText
                    primary={section.label}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                )}
              </ListItemButton>
            );
            return slim ? (
              <Tooltip key={section.label} title={section.label} placement="right">
                {button}
              </Tooltip>
            ) : (
              button
            );
          }

          if (slim) {
            return (
              <Tooltip key={section.label} title={section.label} placement="right">
                <ListItemButton
                  selected={sectionHasActive(section)}
                  onClick={(e) =>
                    setFlyout({ anchor: e.currentTarget, section })
                  }
                  sx={baseItemSx}
                >
                  <ListItemIcon sx={iconColor}>{section.icon}</ListItemIcon>
                </ListItemButton>
              </Tooltip>
            );
          }

          const open = openSections[section.label];
          return (
            <Box key={section.label} sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => toggleSection(section.label)}
                selected={sectionHasActive(section) && !open}
                sx={baseItemSx}
              >
                <ListItemIcon sx={iconColor}>{section.icon}</ListItemIcon>
                <ListItemText
                  primary={section.label}
                  primaryTypographyProps={{ fontWeight: 700 }}
                />
                {open ? (
                  <ExpandLess sx={{ color: zen.sidebarTextMuted }} />
                ) : (
                  <ExpandMore sx={{ color: zen.sidebarTextMuted }} />
                )}
              </ListItemButton>

              <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pb: 0.5 }}>
                  {section.children.map((child) => (
                    <ListItemButton
                      key={child.label}
                      onClick={() => goTo(child.route)}
                      selected={isActive(child.route)}
                      sx={{
                        pl: 6,
                        mx: 1,
                        borderRadius: 1.5,
                        color: zen.sidebarTextMuted,
                        '&:hover': { backgroundColor: zen.sidebarHover },
                        '&.Mui-selected': {
                          backgroundColor: zen.sidebarSelected,
                          color: zen.sidebarSelectedText,
                          '& .MuiListItemIcon-root': {
                            color: zen.sidebarSelectedText,
                          },
                          '&:hover': { backgroundColor: zen.sidebarSelectedHover },
                        },
                      }}
                    >
                      {child.icon && (
                        <ListItemIcon sx={{ color: zen.sidebarIcon, minWidth: 32 }}>
                          {child.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      <Divider sx={{ borderColor: zen.sidebarDivider }} />

      {isDesktop && slim && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Tooltip title="Expandir menu" placement="right">
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              sx={{ color: zen.sidebarTextMuted }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box
        sx={{
          p: slim ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: slim ? 'center' : 'space-between',
          flexDirection: slim ? 'column' : 'row',
          gap: slim ? 1 : 0,
          color: zen.sidebarText,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Tooltip title={slim ? userName : ''} placement="right">
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: 14,
                fontWeight: 700,
                bgcolor: zen.sidebarAvatar,
                color: zen.sidebarAvatarText,
              }}
            >
              {initials}
            </Avatar>
          </Tooltip>
          {!slim && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap fontWeight={600} sx={{ color: zen.sidebarText }}>
                {userName}
              </Typography>
              <Typography variant="caption" sx={{ color: zen.sidebarTextMuted }}>
                Conectado
              </Typography>
            </Box>
          )}
        </Box>
        <Tooltip title="Sair" placement={slim ? 'right' : 'bottom'}>
          <IconButton size="small" onClick={handleLogout} sx={{ color: zen.sidebarTextMuted }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {!slim && (
        <Box textAlign="center" fontSize={12} sx={{ color: zen.sidebarTextMuted, pb: 1.5 }}>
          Powered by <strong style={{ color: zen.sidebarText }}>ZenSoftware</strong> © 2025
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: zen.navbarBackground,
          color: zen.navbarText,
          height: 64,
          borderBottom: `1px solid ${zen.navbarBorder}`,
          boxShadow: zen.navbarShadow,
          width: isDesktop ? `calc(100% - ${width}px)` : '100%',
          ml: isDesktop ? `${width}px` : 0,
          transition: (t) =>
            t.transitions.create(['width', 'margin'], {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton color="inherit" edge="start" onClick={onMenuClick}>
              <MenuIcon />
            </IconButton>
            {!isDesktop && (
              <Box
                component="img"
                src={zen.navbarLogo}
                alt="ZenGestão"
                draggable={false}
                onClick={() => navigate('/dashboard')}
                sx={{ height: 28, width: 'auto', objectFit: 'contain', cursor: 'pointer', ml: 0.5 }}
              />
            )}
            {isDesktop && (
              <Box sx={{ ml: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 700,
                    color: zen.navbarTextMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.16em',
                    lineHeight: 1,
                    fontSize: 9,
                    mb: 0.25,
                  }}
                >
                  {getAuthSlug() || 'ZenGestão'}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    lineHeight: 1.2,
                    fontSize: 12,
                  }}
                >
                  Painel ZenGestão
                </Typography>
              </Box>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            {estabelecimentos.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={estabelecimentoId || ''}
                  onChange={(e) => selecionarEstabelecimento(e.target.value)}
                  displayEmpty
                  disabled={carregandoEstabs}
                  sx={{
                    color: 'inherit',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: zen.navbarBorder },
                    fontSize: 13,
                  }}
                >
                  {estabelecimentos.map((e) => (
                    <MenuItem key={e.id} value={e.id} disabled={e.ativo === false}>
                      {e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`}
                      {e.ativo === false ? ' (inativa)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <ThemeModeToggle />
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop || drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            backgroundColor: zen.sidebarBackground,
            color: zen.sidebarText,
            borderRight: `1px solid ${zen.sidebarBorder}`,
            overflowX: 'hidden',
            transition: (t) =>
              t.transitions.create('width', {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Menu
        anchorEl={flyout.anchor}
        open={Boolean(flyout.anchor && flyout.section)}
        onClose={() => setFlyout({ anchor: null, section: null })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {flyout.section?.children?.map((child) => (
          <MenuItem
            key={child.label}
            selected={isActive(child.route)}
            onClick={() => goTo(child.route)}
          >
            {child.icon && (
              <ListItemIcon sx={{ minWidth: 36 }}>{child.icon}</ListItemIcon>
            )}
            {child.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default Navbar;
